// Visualizador de documentos reutilizável (admin, painel da babá, painel da
// família). Recebe um usuário (com foto_url + documentos_url) e o papel,
// resolve os paths, gera URLs assinadas e renderiza cada documento com
// preview e botão "Abrir". Ver documents.js para a lógica de resolução.
import React, { useEffect, useState } from "react";
import { FileText, ExternalLink, ImageOff } from "lucide-react";
import { buildDocList, signDocumentUrl, isPdfPath } from "./documents";

function DocCard({ slot, loading }) {
  const enviado = Boolean(slot.path);
  const pdf = isPdfPath(slot.path);
  const [imgError, setImgError] = useState(false);
  const showImage = enviado && slot.signedUrl && !pdf && !imgError;
  const canOpen = enviado && slot.signedUrl;

  const preview = (
    <div className="relative aspect-3/4 w-full overflow-hidden rounded-xl border border-warm-200 bg-warm-50">
      {showImage ? (
        <img
          src={slot.signedUrl}
          alt={slot.label}
          className="h-full w-full object-cover transition group-hover:scale-[1.03]"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          {enviado ? (
            <FileText size={30} className="text-warm-400" />
          ) : (
            <ImageOff size={28} className="text-warm-300" />
          )}
        </div>
      )}
      <span
        className={`absolute right-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm ${
          enviado ? "bg-emerald-500 text-white" : "bg-warm-400 text-white"
        }`}
      >
        {enviado ? "Enviado" : "Faltando"}
      </span>
      {canOpen && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-warm-900/0 opacity-0 transition group-hover:bg-warm-900/25 group-hover:opacity-100">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-warm-800 shadow">
            <ExternalLink size={12} /> Abrir
          </span>
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {canOpen ? (
        <a
          href={slot.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={`Abrir ${slot.label}`}
          className="group block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
        >
          {preview}
        </a>
      ) : (
        preview
      )}

      <span className="text-xs font-semibold leading-tight text-warm-800">
        {slot.label}
      </span>

      {canOpen ? (
        <a
          href={slot.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Abrir ${slot.label} em nova aba`}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-warm-300 px-2.5 py-2 text-xs font-semibold text-warm-800 transition hover:bg-warm-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
        >
          <ExternalLink size={14} aria-hidden="true" /> Abrir
        </a>
      ) : (
        <span className="inline-flex items-center justify-center rounded-lg border border-dashed border-warm-200 px-2.5 py-2 text-xs font-medium text-warm-500">
          {enviado ? (loading ? "Carregando…" : "Indisponível") : "Não enviado"}
        </span>
      )}
    </div>
  );
}

export default function DocumentsViewer({ user, role = "baba", className = "" }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const base = buildDocList(user, role);
    setSlots(base); // mostra estrutura/status imediatamente
    setLoading(true);

    (async () => {
      const signed = await Promise.all(
        base.map(async (s) => ({
          ...s,
          signedUrl: s.path ? await signDocumentUrl(s.path) : null,
        })),
      );
      if (!cancelled) {
        setSlots(signed);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, role]);

  return (
    <div
      className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 ${className}`}
    >
      {slots.map((slot) => (
        <DocCard key={slot.key} slot={slot} loading={loading} />
      ))}
    </div>
  );
}
